export type ConsumerChampionSentiment = 'positive' | 'neutral' | 'negative';
export type ConsumerChampionTrend = 'growing' | 'stable' | 'decreasing';

export interface ProductInsight {
  id: string;
  title: string;
  description: string;
  sentiment: ConsumerChampionSentiment;
  score: number;
  count: number;
  category: string;
  topic: string;
  firstDetected: string;
  lastDetected: string;
  feedbackSources: string[];
  impactStatus: ConsumerChampionTrend;
  keyPoints: string[];
  suggestedActions: string[];
}

export interface FeedbackItem {
  id: string;
  insightId: string;
  customer: string;
  date: string;
  source: string;
  verbatim: string;
  sentiment: ConsumerChampionSentiment;
  sentimentScore: number;
  topEmotions: string[];
  keywords: string[];
  category: string;
  topic: string;
}

export const productInsights: ProductInsight[] = [
    {
      id: 'INS-001',
      title: 'Mobile App Navigation Loved',
      description: 'Multiple users praising the intuitive navigation in recent app update',
      sentiment: 'positive',
      score: 92,
      count: 157,
      category: 'Mobile App',
      topic: 'Navigation',
      firstDetected: '2025-04-10T08:30:00',
      lastDetected: '2025-04-29T15:45:00',
      feedbackSources: ['App Store Reviews', 'In-app Feedback', 'Social Media'],
      impactStatus: 'growing',
      keyPoints: [
        'New bottom navigation widely praised',
        'Quick access to accounts feature highlighted',
        'Transaction search simplicity mentioned frequently'
      ],
      suggestedActions: [
        'Share positive feedback with product and design teams',
        'Highlight in next customer newsletter',
        'Consider expanding navigation pattern to other products'
      ]
    },
    {
      id: 'INS-002',
      title: 'Loan Application Process Pain Points',
      description: 'Customers reporting friction points in digital loan application',
      sentiment: 'negative',
      score: 78,
      count: 83,
      category: 'Lending',
      topic: 'Application Process',
      firstDetected: '2025-04-15T12:10:00',
      lastDetected: '2025-04-28T09:45:00',
      feedbackSources: ['Customer Support', 'NPS Survey Responses', 'Direct Feedback'],
      impactStatus: 'stable',
      keyPoints: [
        'Document upload process causing frustration',
        'Lack of progress saving feature mentioned often',
        'Unclear eligibility requirements during process'
      ],
      suggestedActions: [
        'Optimize document upload experience',
        'Add save & resume functionality',
        'Clarify eligibility criteria earlier in flow',
        'Create guided tutorial for first-time applicants'
      ]
    },
    {
      id: 'INS-003',
      title: 'Online Statement Features Appreciated',
      description: 'Positive feedback on new statement filters and export options',
      sentiment: 'positive',
      score: 89,
      count: 112,
      category: 'Online Banking',
      topic: 'Statements & Documents',
      firstDetected: '2025-04-05T14:20:00',
      lastDetected: '2025-04-29T11:30:00',
      feedbackSources: ['NPS Survey Responses', 'Email Feedback', 'Customer Support'],
      impactStatus: 'growing',
      keyPoints: [
        'CSV export functionality particularly appreciated',
        'Category filtering saving time for users',
        'Search within statements helping find specific transactions'
      ],
      suggestedActions: [
        'Expand export options (PDF, spreadsheet formats)',
        'Consider adding tagging functionality',
        'Highlight these features in customer communications'
      ]
    },
    {
      id: 'INS-004',
      title: 'Rewards Program Confusion',
      description: 'Uncertainty about how rewards points are calculated and redeemed',
      sentiment: 'neutral',
      score: 65,
      count: 94,
      category: 'Rewards Program',
      topic: 'Points System',
      firstDetected: '2025-04-08T09:15:00',
      lastDetected: '2025-04-27T16:45:00',
      feedbackSources: ['Call Center', 'Live Chat Transcripts', 'App Feedback'],
      impactStatus: 'decreasing',
      keyPoints: [
        'Points calculation not transparent to users',
        'Expiration policies causing frustration',
        'Redemption process seen as overly complex'
      ],
      suggestedActions: [
        'Create simplified explanation of points system',
        'Add points calculator tool for transparency',
        'Streamline redemption process',
        'Send notifications before points expire'
      ]
    },
    {
      id: 'INS-005',
      title: 'Biometric Authentication Praised',
      description: 'Strong positive feedback on fingerprint and face ID login options',
      sentiment: 'positive',
      score: 95,
      count: 203,
      category: 'Security',
      topic: 'Authentication',
      firstDetected: '2025-04-01T10:30:00',
      lastDetected: '2025-04-29T13:15:00',
      feedbackSources: ['App Reviews', 'Social Media', 'Customer Surveys'],
      impactStatus: 'stable',
      keyPoints: [
        'Speed of login process frequently mentioned',
        'Combination of security and convenience appreciated',
        'Cross-device consistency highlighted'
      ],
      suggestedActions: [
        'Explore expanding biometric options to more services',
        'Highlight security and convenience in marketing',
        'Share positive impact metrics with security team'
      ]
    },
    {
      id: 'INS-006',
      title: 'Notification Preferences Need Improvement',
      description: 'Feedback suggests more granular control over banking notifications needed',
      sentiment: 'negative',
      score: 72,
      count: 67,
      category: 'Mobile App',
      topic: 'Notifications',
      firstDetected: '2025-04-12T16:20:00',
      lastDetected: '2025-04-28T14:45:00',
      feedbackSources: ['App Feedback', 'Customer Support', 'Social Media'],
      impactStatus: 'growing',
      keyPoints: [
        'Too frequent alerts causing notification fatigue',
        'Limited ability to customize notification types',
        'Inconsistent delivery across channels'
      ],
      suggestedActions: [
        'Redesign notification preference center',
        'Add time-based quiet periods option',
        'Create category-based notification bundling',
        'Allow channel-specific preferences'
      ]
    },
    {
      id: 'INS-007',
      title: 'Budgeting Tools Highly Valued',
      description: 'Users expressing appreciation for expense categorization and budget alerts',
      sentiment: 'positive',
      score: 88,
      count: 135,
      category: 'Financial Management',
      topic: 'Budgeting',
      firstDetected: '2025-04-03T11:45:00',
      lastDetected: '2025-04-29T10:30:00',
      feedbackSources: ['App Feedback', 'NPS Surveys', 'Social Media'],
      impactStatus: 'growing',
      keyPoints: [
        'Automatic categorization saving time for users',
        'Budget alerts helping manage spending',
        'Visualization of spending patterns appreciated'
      ],
      suggestedActions: [
        'Add more granular subcategories',
        'Consider adding savings goals feature',
        'Introduce budgeting insights and tips',
        'Explore personalized financial advice based on patterns'
      ]
    },
    {
      id: 'INS-008',
      title: 'International Transfer Experience Frustrating',
      description: 'Feedback indicates process for sending money abroad is cumbersome',
      sentiment: 'negative',
      score: 82,
      count: 76,
      category: 'Payments',
      topic: 'International Transfers',
      firstDetected: '2025-04-07T09:50:00',
      lastDetected: '2025-04-28T16:15:00',
      feedbackSources: ['Branch Feedback', 'Call Center', 'Customer Complaints'],
      impactStatus: 'stable',
      keyPoints: [
        'Exchange rate transparency concerns',
        'Too many steps in transfer process',
        'Uncertainty about transfer timing',
        'Verification requirements seen as excessive'
      ],
      suggestedActions: [
        'Streamline international transfer workflow',
        'Add real-time tracking for transfers',
        'Provide clearer information on exchange rates and fees',
        'Review verification steps for necessary vs. excessive'
      ]
    }
  ];

export const feedbackItems: FeedbackItem[] = [
    {
      id: 'FB-1001',
      insightId: 'INS-001',
      customer: 'Alex Johnson',
      date: '2025-04-28T14:35:00',
      source: 'App Store Review',
      verbatim: "The new navigation in the app is a game-changer! I can finally find everything I need without having to dig through menus. The quick access to accounts and spending insights is brilliant.",
      sentiment: 'positive',
      sentimentScore: 9.2,
      topEmotions: ['Satisfaction', 'Appreciation', 'Relief'],
      keywords: ['navigation', 'quick access', 'accounts', 'brilliant'],
      category: 'Mobile App',
      topic: 'Navigation'
    },
    {
      id: 'FB-1002',
      insightId: 'INS-001',
      customer: 'Maria Rodriguez',
      date: '2025-04-26T09:20:00',
      source: 'In-app Feedback',
      verbatim: "Just updated to the new version and love how easy it is to move between accounts now. The bottom navigation makes so much more sense than the old menu.",
      sentiment: 'positive',
      sentimentScore: 8.7,
      topEmotions: ['Happiness', 'Satisfaction'],
      keywords: ['bottom navigation', 'accounts', 'easy'],
      category: 'Mobile App',
      topic: 'Navigation'
    },
    {
      id: 'FB-1003',
      insightId: 'INS-002',
      customer: 'David Chen',
      date: '2025-04-27T16:45:00',
      source: 'Customer Support Chat',
      verbatim: "I'm trying to apply for a personal loan but keep losing all my progress when I try to upload documents. This is the third time I've had to start over and it's extremely frustrating. Why can't I save my application and come back to it?",
      sentiment: 'negative',
      sentimentScore: 2.1,
      topEmotions: ['Frustration', 'Irritation', 'Annoyance'],
      keywords: ['loan application', 'losing progress', 'upload documents', 'start over'],
      category: 'Lending',
      topic: 'Application Process'
    },
    {
      id: 'FB-1004',
      insightId: 'INS-002',
      customer: 'Sarah Williams',
      date: '2025-04-25T11:30:00',
      source: 'NPS Survey',
      verbatim: "The loan application takes way too long to complete. The document upload feature crashed twice on me, and I had to re-scan everything. I almost gave up and went to another bank.",
      sentiment: 'negative',
      sentimentScore: 2.4,
      topEmotions: ['Frustration', 'Disappointment'],
      keywords: ['loan application', 'document upload', 'crashed', 're-scan'],
      category: 'Lending',
      topic: 'Application Process'
    },
    {
      id: 'FB-1005',
      insightId: 'INS-003',
      customer: 'Thomas Brown',
      date: '2025-04-29T08:15:00',
      source: 'Email Feedback',
      verbatim: "Just discovered I can export my statements to CSV and filter by category. This has saved me hours of work for my expense tracking! Whoever added this feature deserves a raise.",
      sentiment: 'positive',
      sentimentScore: 9.5,
      topEmotions: ['Excitement', 'Gratitude', 'Relief'],
      keywords: ['export', 'CSV', 'filter', 'category', 'expense tracking'],
      category: 'Online Banking',
      topic: 'Statements & Documents'
    },
    {
      id: 'FB-1006',
      insightId: 'INS-004',
      customer: 'James Wilson',
      date: '2025-04-23T15:10:00',
      source: 'Call Center',
      verbatim: "I've been using my card for months but I have no idea how many points I've earned or how they're calculated. The website doesn't explain it clearly and I can't figure out how to actually use my points.",
      sentiment: 'neutral',
      sentimentScore: 5.2,
      topEmotions: ['Confusion', 'Curiosity', 'Mild Frustration'],
      keywords: ['points', 'calculated', 'how to use', 'not clear'],
      category: 'Rewards Program',
      topic: 'Points System'
    },
    {
      id: 'FB-1007',
      insightId: 'INS-005',
      customer: 'Emily Thompson',
      date: '2025-04-28T12:45:00',
      source: 'App Review',
      verbatim: "The fingerprint login is fantastic! So much faster than typing a password, and I love that it works the same way on both my phone and tablet. Feels secure but convenient.",
      sentiment: 'positive',
      sentimentScore: 9.7,
      topEmotions: ['Satisfaction', 'Trust', 'Relief'],
      keywords: ['fingerprint login', 'faster', 'secure', 'convenient'],
      category: 'Security',
      topic: 'Authentication'
    },
    {
      id: 'FB-1008',
      insightId: 'INS-006',
      customer: 'Michael Garcia',
      date: '2025-04-26T19:20:00',
      source: 'App Feedback',
      verbatim: "I get way too many notifications from the banking app. I want transaction alerts, but not marketing messages or 'tips'. Please let me choose exactly what I get notified about and when.",
      sentiment: 'negative',
      sentimentScore: 3.8,
      topEmotions: ['Annoyance', 'Frustration'],
      keywords: ['too many notifications', 'transaction alerts', 'choose', 'marketing messages'],
      category: 'Mobile App',
      topic: 'Notifications'
    }
  ];
